/**
 * Anonymous storefront session tracking.
 *
 * A single UUID per browser, persisted in localStorage with a sliding 30-day
 * expiry. Attached to every try-on / sizing / event call the SDK makes so the
 * backend can stitch a customer's actions together without ever knowing their
 * identity.
 */

const STORAGE_KEY = "ps_session";
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const FALLBACK_PREFIX = "ps_mem_";

let memoryFallback: SessionRecord | null = null;

interface SessionRecord {
	id: string;
	issuedAt: number;
	lastSeenAt: number;
}

function hasLocalStorage(): boolean {
	try {
		return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
	} catch {
		return false;
	}
}

function uuid(): string {
	// Prefer crypto.randomUUID when available. Fall back to a good-enough v4
	// based on Math.random — this is an opaque anonymous id, not a security token.
	if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
		return crypto.randomUUID();
	}
	const chars = "0123456789abcdef";
	let out = FALLBACK_PREFIX;
	for (let i = 0; i < 32; i++) out += chars[Math.floor(Math.random() * 16)];
	return out;
}

function readRecord(): SessionRecord | null {
	if (!hasLocalStorage()) return memoryFallback;
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as Partial<SessionRecord>;
		if (typeof parsed.id !== "string" || typeof parsed.issuedAt !== "number") return null;
		return {
			id: parsed.id,
			issuedAt: parsed.issuedAt,
			lastSeenAt: typeof parsed.lastSeenAt === "number" ? parsed.lastSeenAt : parsed.issuedAt,
		};
	} catch {
		return null;
	}
}

function writeRecord(record: SessionRecord): void {
	memoryFallback = record;
	if (!hasLocalStorage()) return;
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
	} catch {
		// Quota full / disabled — keep memory fallback.
	}
}

function attachReplaySession(sessionId: string): void {
	if (typeof window === "undefined") return;
	try {
		const clarity = (window as unknown as { clarity?: (...args: unknown[]) => void }).clarity;
		if (typeof clarity === "function") clarity("set", STORAGE_KEY, sessionId);
	} catch {
		// Analytics correlation must never affect SDK UX.
	}
}

/**
 * Return the current session id, creating one if absent or expired.
 * Safe to call repeatedly — cheap local-storage read + occasional write.
 */
export function getOrCreateSessionId(): string {
	const now = Date.now();
	const existing = readRecord();
	if (existing && now - existing.lastSeenAt < MAX_AGE_MS) {
		// Slide the expiry forward, but don't write more than once per 5 minutes.
		if (now - existing.lastSeenAt > 5 * 60 * 1000) {
			writeRecord({ ...existing, lastSeenAt: now });
		}
		attachReplaySession(existing.id);
		return existing.id;
	}
	const fresh: SessionRecord = { id: uuid(), issuedAt: now, lastSeenAt: now };
	writeRecord(fresh);
	attachReplaySession(fresh.id);
	return fresh.id;
}

/**
 * Best-effort device hint derived from userAgent. The backend has a full
 * UA parser; this is purely for servers that fall back to client-reported
 * hints (e.g. rate-limited environments with no UA header).
 */
export function getDeviceHint(): "mobile" | "tablet" | "desktop" | null {
	if (typeof navigator === "undefined") return null;
	const ua = (navigator.userAgent || "").toLowerCase();
	if (/ipad|tablet|(android(?!.*mobile))/.test(ua)) return "tablet";
	if (/mobile|android|iphone|ipod|blackberry|iemobile|opera mini/.test(ua)) return "mobile";
	return "desktop";
}
